import { Inject, Injectable } from '@angular/core';
import { Environment, ENVIRONMENT } from '@sondermind/utilities/models-environment';
import { IDataLayer, IUserLoadedDataLayer } from '../models/gtm-data-layer.interface';
import {
  GtmCustomEvents,
  GtmExternalCalActions,
  GtmIntakeActions,
  GtmSamActions
} from '../models/gtm-events.enum';

interface IGtmLayerWindow extends Window {
  dataLayer?: IDataLayer[];
}

declare let window: IGtmLayerWindow;

@Injectable({
  providedIn: 'root'
})
export class GtmDataLayerService {
  constructor(
    @Inject(ENVIRONMENT)
    private env: Environment
  ) {}

  set dataLayer(data: IDataLayer) {
    // eslint-disable-next-line no-extra-boolean-cast
    if (!!window.dataLayer) {
      window.dataLayer.push(data);
    }
  }

  /*
     * wrapper method mainly used by GoogleTagManagerSamDirective
     * send the current timestamp in the label field for easier analysis
     * of time spent on each screen
     */
  sendSamSignupEvent(action: GtmSamActions): void {
    this.dataLayer = {
      event: GtmCustomEvents.CLIENT_SAM_SIGNUP,
      category: GtmCustomEvents.CLIENT_SAM_SIGNUP,
      action,
      label: this.getCurrentISOTime()
    };
  }

  sendUserCompletedAccount(): void {
    this.dataLayer = {
      event: GtmCustomEvents.CLIENT_ACCOUNT_COMPLETE,
      category: GtmCustomEvents.CLIENT_ACCOUNT_COMPLETE,
      action: GtmIntakeActions.SETUP_COMPLETE,
      label: 'account-complete'
    };
  }

  sendUserTherapyReadiness(eventType: string, label: string): void {
    this.dataLayer = {
      event: eventType,
      category: eventType,
      action: GtmIntakeActions.SUBMIT_THERAPY_READINESS,
      label
    };
  }

  sendClientClaimExists(): void {
    this.dataLayer = {
      event: GtmCustomEvents.CLIENT_CLAIM_SUBMITTED,
      category: GtmCustomEvents.CLIENT_CLAIM_SUBMITTED,
      action: GtmIntakeActions.CLAIM_SUBMITTED,
      label: 'claim-submitted'
    };
  }

  /*
     * wrapper for better unit testing
     */
  getCurrentISOTime(): string {
    const currentDate = new Date();

    return currentDate.toISOString();
  }

  sendCalEvent(action: GtmExternalCalActions): void {
    this.dataLayer = {
      event: GtmCustomEvents.CAL_CONNECT_CLICKED,
      category: GtmCustomEvents.CAL_CONNECT_CLICKED,
      action,
      label: this.getCurrentISOTime()
    };
  }

  sendCalType(calType: GtmExternalCalActions): void {
    this.dataLayer = {
      event: GtmCustomEvents.CAL_EVENT_CLICKED,
      category: GtmCustomEvents.CAL_EVENT_CLICKED,
      type: calType,
      label: this.getCurrentISOTime()
    };
  }

  sendDefaultFontSize(fontSize: string): void {
    this.dataLayer = {
      event: GtmCustomEvents.USER_SETTINGS_DEFAULT_FONT_SIZE,
      category: GtmCustomEvents.USER_SETTINGS_DEFAULT_FONT_SIZE,
      label: fontSize
    };
  }

  sendUserLoadedEvent(values: IUserLoadedDataLayer): void {
    let idWithPrefix: string = '';
    if (this.env.staging) {
      idWithPrefix = `staging-${values.user_id}`;
      // eslint-disable-next-line no-param-reassign
      values.user_id = idWithPrefix;
    }
    this.dataLayer = values;
  }
}
